import { Button, Upload, Typography } from 'antd';
import type { UploadProps } from 'antd';
import { createStyles } from 'antd-style';
import type { ReactNode } from 'react';
import { useState } from 'react';
import VideoPlayer from '../VideoPlayer';

const { Dragger } = Upload;
const { Text } = Typography;

const useStyles = createStyles(({ token, css }) => ({
  dragger: css`
    .ant-upload-drag {
      min-height: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `,
  preview: css`
    position: relative;
    width: 100%;
    border-radius: ${token.borderRadiusLG}px;
    overflow: hidden;
    img,
    video {
      width: 100%;
      display: block;
      object-fit: cover;
    }
  `,
  replace: css`
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 2;
  `,
  inline: css`
    display: inline-flex;
    align-items: center;
    gap: ${token.marginSM}px;
  `,
  thumb: css`
    width: 104px;
    height: 72px;
    border-radius: ${token.borderRadiusLG}px;
    object-fit: cover;
  `,
  emptyThumb: css`
    background: ${token.colorFillSecondary};
    border: 1px solid ${token.colorBorderSecondary};
  `,
}));

export type MediaKind = 'image' | 'video' | 'any';

export interface MediaUploaderProps {
  value?: string;
  onChange?: (url?: string) => void;
  upload: (file: File) => Promise<string | undefined>;
  kind?: MediaKind;
  maxBytes?: number;
  sourceLabel?: ReactNode;
  buttonText?: ReactNode;
  replaceText?: ReactNode;
  uploadingText?: ReactNode;
  hint?: ReactNode;
  invalidTypeMessage?: string;
  tooLargeMessage?: string;
  onError?: (message: string) => void;
  mode?: 'dragger' | 'button';
  accept?: string;
  disabled?: boolean;
  previewHeight?: number;
  uploadProps?: Omit<UploadProps, 'beforeUpload' | 'showUploadList' | 'accept'>;
}

function matchesKind(file: File, kind: MediaKind) {
  if (kind === 'any') return true;
  return file.type.startsWith(`${kind}/`);
}

export default function MediaUploader({
  value,
  onChange,
  upload,
  kind = 'image',
  maxBytes,
  sourceLabel,
  buttonText = '上传',
  replaceText = '重新上传',
  uploadingText = '上传中…',
  hint = '点击或拖拽上传',
  invalidTypeMessage = '文件类型不正确',
  tooLargeMessage = '文件过大',
  onError,
  mode = 'dragger',
  accept,
  disabled,
  previewHeight,
  uploadProps,
}: MediaUploaderProps) {
  const { styles, cx } = useStyles();
  const [uploading, setUploading] = useState(false);
  const realAccept = accept || (kind === 'any' ? undefined : `${kind}/*`);

  const beforeUpload = async (file: File) => {
    if (!matchesKind(file, kind)) {
      onError?.(invalidTypeMessage);
      return Upload.LIST_IGNORE;
    }
    if (maxBytes && file.size > maxBytes) {
      onError?.(tooLargeMessage);
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    try {
      const url = await upload(file);
      if (url) onChange?.(url);
    } catch (e) {
      onError?.((e as Error)?.message || '上传失败');
    } finally {
      setUploading(false);
    }
    return Upload.LIST_IGNORE;
  };

  if (mode === 'button') {
    return (
      <span className={styles.inline}>
        {value ? (
          kind === 'video' ? (
            // biome-ignore lint/a11y/useMediaCaption: 仅作为上传预览
            <video className={styles.thumb} src={value} />
          ) : (
            <img className={styles.thumb} src={value} alt="" />
          )
        ) : (
          <span className={cx(styles.thumb, styles.emptyThumb)} />
        )}
        <Upload
          showUploadList={false}
          accept={realAccept}
          beforeUpload={beforeUpload}
          disabled={disabled || uploading}
          {...uploadProps}
        >
          <Button loading={uploading} disabled={disabled}>
            {uploading ? uploadingText : buttonText}
          </Button>
        </Upload>
        {sourceLabel ? <Text type="secondary">{sourceLabel}</Text> : null}
      </span>
    );
  }

  if (value) {
    return (
      <div className={styles.preview} style={{ height: previewHeight }}>
        <Button
          className={styles.replace}
          size="small"
          onClick={() => onChange?.(undefined)}
        >
          {replaceText}
        </Button>
        {kind === 'video' ? (
          <VideoPlayer
            src={value}
            preview
            style={previewHeight ? { height: '100%' } : undefined}
          />
        ) : (
          <img src={value} alt="" />
        )}
      </div>
    );
  }

  return (
    <Dragger
      className={styles.dragger}
      accept={realAccept}
      showUploadList={false}
      disabled={disabled || uploading}
      beforeUpload={beforeUpload}
      {...uploadProps}
    >
      <div>
        <p style={{ fontSize: 22, marginBottom: 4 }}>{sourceLabel}</p>
        <p>{uploading ? uploadingText : hint}</p>
      </div>
    </Dragger>
  );
}
